import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {FlexColumn, NestedCard} from '@forecast-it/design-system';
import {EstimateRowsPatch, EstimateSections} from './EstimateSections';
import {DeliverableDraft, ExpenseEstimateDraft, Option, TimeEstimateDraft} from './baselineTemplateDraft';

// NestedCard's content padding is tight for a form, so the body adds its own.
const Body = Styled(FlexColumn)`
	padding: 8px;
`;

type Props = {
	cyPrefix: string;
	/** Null draws the phase's No deliverable card: its lines at phase level. */
	deliverable: DeliverableDraft | null;
	/** There is another template deliverable to change to. */
	canChange: boolean;
	timeEstimates: TimeEstimateDraft[];
	expenseEstimates: ExpenseEstimateDraft[];
	roles: Option[];
	categories: Option[];
	heldRoles?: Readonly<Record<string, Option>>;
	heldCategories?: Readonly<Record<string, Option>>;
	currency: string;
	fieldError: (path: string) => string | undefined;
	onChangeRows: (patch: EstimateRowsPatch, changedPath?: string) => void;
	onChangeDeliverable: () => void;
	/** Not offered on the No deliverable card. */
	onEditDeliverable?: () => void;
	onRemoveFromPhase?: () => void;
};

/** One deliverable inside a phase, with its own lines, laid out as on the Baseline edit page. Templates hold no rates. */
export const TemplateDeliverableCard = ({
	cyPrefix,
	deliverable,
	canChange,
	timeEstimates,
	expenseEstimates,
	roles,
	categories,
	heldRoles,
	heldCategories,
	currency,
	fieldError,
	onChangeRows,
	onChangeDeliverable,
	onEditDeliverable,
	onRemoveFromPhase,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const contextMenuOptions = [
		{name: formatMessage({id: 'deliverable.change_deliverable'}), onClick: onChangeDeliverable, disabled: !canChange},
		...(deliverable && onEditDeliverable && onRemoveFromPhase
			? [
					{name: formatMessage({id: 'deliverables.edit.modal.edit_deliverable'}), onClick: onEditDeliverable},
					{name: formatMessage({id: 'deliverable.remove_deliverable_from_phase'}), onClick: onRemoveFromPhase},
			  ]
			: []),
	];
	return (
		<NestedCard colorTheme="medium" dataCy={`${cyPrefix}-card`}>
			<NestedCard.Header
				{...(deliverable
					? {mainIcon: 'deliverable', sectionName: formatMessage({id: 'common.deliverable'})}
					: {sectionName: formatMessage({id: 'deliverable.no_deliverable'})})}
				name={deliverable?.name ?? ''}
				showContextMenu
				contextMenuOptions={contextMenuOptions}
			/>
			<NestedCard.UncollapsibleContentWrapper>
				<NestedCard.Content>
					<Body gap="l">
						<EstimateSections
							cyPrefix={cyPrefix}
							deliverableKey={deliverable?.key ?? null}
							timeEstimates={timeEstimates}
							expenseEstimates={expenseEstimates}
							roles={roles}
							categories={categories}
							heldRoles={heldRoles}
							heldCategories={heldCategories}
							currency={currency}
							fieldError={fieldError}
							onChange={onChangeRows}
						/>
					</Body>
				</NestedCard.Content>
			</NestedCard.UncollapsibleContentWrapper>
		</NestedCard>
	);
};
